import { X } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ApiError } from "@/api/client";
import { useCreateClient, useUpdateClient } from "@/api/hooks";
import type { ClientAddressFields, ClientResponse, OrganizationSummary } from "@/api/types";
import { AddressInput } from "@/components/AddressInput";
import { PhoneInput } from "@/components/PhoneInput";
import { formatPhoneForDisplay } from "@/lib/phoneFormat";

function emptyAddress(): ClientAddressFields {
  return {
    address_line1: null,
    address_line2: null,
    city: null,
    region: null,
    postal_code: null,
    country: null,
  };
}

export interface ClientFormProps {
  mode: "create" | "edit";
  /** Required when ``mode === "edit"``. */
  client?: ClientResponse;
  /** True when caller is ``pencilout_admin`` — adds the org picker
   * to the create-mode form. */
  isPencilOutAdmin: boolean;
  /** Org list for the picker. Empty in non-admin views. */
  orgs: OrganizationSummary[];
  /** Cancel + post-save handler. The detail page wires this to flip
   * back from edit → view; the list page collapses the inline row. */
  onClose: () => void;
}

/** Reusable client editor — shared between the inline row-edit on
 * ClientsPage and the dedicated ClientDetailPage. */
export function ClientForm({ mode, client, isPencilOutAdmin, orgs, onClose }: ClientFormProps) {
  const create = useCreateClient();
  const update = useUpdateClient(client?.id ?? "");
  const mutation = mode === "create" ? create : update;

  const [orgId, setOrgId] = useState(client?.organization_id ?? "");
  const [name, setName] = useState(client?.name ?? "");
  const [email, setEmail] = useState(client?.email ?? "");
  // Pre-format any stored E.164 (``+17045551234``) into display
  // form (``(704) 555-1234``) on first render so edit-mode shows
  // the same masked shape the create form uses.
  const [phone, setPhone] = useState(formatPhoneForDisplay(client?.phone));
  const [address, setAddress] = useState<ClientAddressFields>(
    client
      ? {
          address_line1: client.address_line1,
          address_line2: client.address_line2,
          city: client.city,
          region: client.region,
          postal_code: client.postal_code,
          country: client.country,
        }
      : emptyAddress(),
  );
  const [notes, setNotes] = useState(client?.notes ?? "");

  const needsOrg = mode === "create" && isPencilOutAdmin;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) return;
    if (needsOrg && !orgId) return;

    const payload = {
      name: name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      ...address,
      notes: notes.trim() || null,
    };

    try {
      if (mode === "create") {
        await create.mutateAsync({
          ...payload,
          organization_id: needsOrg ? orgId : undefined,
        });
      } else {
        await update.mutateAsync(payload);
      }
      onClose();
    } catch {
      /* surfaced below */
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider">
          {mode === "create" ? "New client" : "Edit client"}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-graphite-soft hover:text-graphite"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {needsOrg && (
          <label className="block md:col-span-2">
            <span className="label-mono">Organization</span>
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              required
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
            >
              <option value="">— select —</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="block">
          <span className="label-mono">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith / Smith Family — Maple Ave"
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="label-mono">Email (optional)</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@example.com"
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <div className="md:col-span-2">
          <label htmlFor="client-phone" className="label-mono block">
            Phone (optional)
          </label>
          <PhoneInput
            id="client-phone"
            value={phone}
            onChange={setPhone}
            placeholder="(555) 555-5555"
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </div>

        <div className="md:col-span-2">
          <AddressInput value={address} onChange={setAddress} />
        </div>

        <label className="block md:col-span-2">
          <span className="label-mono">Internal notes (optional)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Repeat customer · introduced by Mike · …"
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>
      </div>

      {mutation.error && (
        <p className="text-sm text-red-pencil">
          {mutation.error instanceof ApiError ? mutation.error.detail : "Could not save client."}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={mutation.isPending || !name.trim() || (needsOrg && !orgId)}
          className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
        >
          {mutation.isPending ? "Saving…" : mode === "create" ? "Create client" : "Save changes"}
        </button>
      </div>
    </form>
  );
}
